import {ssrSafeDocument} from '@github-ui/ssr-utils'

type DataRouterMatch = {
  route?: {id?: string}
  staticData?: {dataRouterId?: string}
}

type DataRouterReactApp = HTMLElement & {
  routerOrHistory?: {state?: {matches?: DataRouterMatch[]}}
}

const ROUTE_ID_META_SELECTOR = 'meta[name="data-router-route-id"]'

export function getCurrentReactAppName() {
  const injectedAppName = ssrSafeDocument?.querySelector('meta[name="react-app-name"]')?.getAttribute('content')

  if (injectedAppName) return injectedAppName

  // special case for memex
  if (ssrSafeDocument?.querySelector('projects-v2')) return 'memex'
  // special case for repos-overview that is a partial
  if (ssrSafeDocument?.querySelector('react-partial[partial-name="repos-overview"]')) return 'repos-overview'

  return ssrSafeDocument?.querySelector('react-app')?.getAttribute('app-name')
}

export function getCurrentRouteId(): string | undefined {
  const reactApp = ssrSafeDocument?.querySelector<DataRouterReactApp>('react-app[data-data-router-enabled="true"]')
  const matches = reactApp?.routerOrHistory?.state?.matches

  if (Array.isArray(matches)) {
    for (let index = matches.length - 1; index >= 0; index--) {
      const match = matches[index]
      const routeId = match?.staticData?.dataRouterId ?? match?.route?.id
      if (routeId) return routeId
    }
  }

  return ssrSafeDocument?.querySelector<HTMLMetaElement>(ROUTE_ID_META_SELECTOR)?.content || undefined
}

/**
 * The shape dotcom accepts for the `experience` tag, mirrored by
 * `GitHub::TaggingHelper::UI_EXPERIENCE_FORMAT` in the monolith.
 *
 * The shape check is the first cardinality bound for client-controlled
 * telemetry; {@link parseExperience} applies the tighter cross-tier allowlist
 * used for values accepted from requests or sent to dotcom.
 */
export const EXPERIENCE_FORMAT = /^[a-z][a-z0-9_]{0,63}$/

/**
 * `codeViewBlobRoute` -> `code_view_blob`.
 *
 * The trailing `Route` is noise once the value is a metric dimension, and every
 * data-router id carries it.
 */
export function normalizeExperience(dataRouterId: string): string {
  return dataRouterId
    .replace(/Route$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
}

/**
 * Tag value for a route id with no reportable form.
 *
 * A sentinel rather than an omitted tag, because Datadog drops untagged points
 * from a `by {experience}` split. A route that stops normalizing would vanish
 * from the breakdown instead of showing up as the bucket to go fix.
 *
 * Shared so the browser and the UI Service spell it the same way. Callers apply
 * it, `toExperience` does not, for the reason documented there.
 */
export const UNRESOLVED_EXPERIENCE = 'unresolved'

/** Code View experiences currently accepted by dotcom for UI compute attribution. */
export const EXPERIENCE_ROUTES = [
  'code_view_blame',
  'code_view_blob_layout',
  'code_view_blob',
  'code_view_delete',
  'code_view_edit',
  'code_view_file_tree_layout',
  'code_view_layout',
  'code_view_new',
  'code_view_repo',
  'code_view_tree_delete',
  'code_view_tree',
] as const

const EXPERIENCE_ROUTE_SET: ReadonlySet<string> = new Set(EXPERIENCE_ROUTES)

export function parseExperience(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  if (!EXPERIENCE_FORMAT.test(value)) return undefined
  if (value === UNRESOLVED_EXPERIENCE) return undefined
  return EXPERIENCE_ROUTE_SET.has(value) ? value : undefined
}

/**
 * A route id in its reportable form, or `undefined` when it has none.
 *
 * Lives here, in a package both tiers can import, because `experience` only
 * works as a join key if the browser and the UI Service derive it from the same
 * route id with the same transform. Two copies of this function would drift into
 * two vocabularies, and the tag would quietly stop joining.
 *
 * Normalization is also why the raw route id cannot serve as the join key
 * directly: Datadog lowercases tag values on ingestion, so `codeViewBlobRoute`
 * arrives as `codeviewblobroute` with its word boundaries destroyed and no way
 * back. Doing the transform before the value leaves the browser is what keeps it
 * readable and comparable.
 *
 * Returns `undefined` rather than {@link UNRESOLVED_EXPERIENCE} so callers can
 * tell "this id has no reportable form" from "this is the value to report".
 * `resolveExperienceFromMatches` needs that distinction to fall through to an
 * ancestor route when a leaf id does not normalize.
 */
export function toExperience(dataRouterId: string): string | undefined {
  const experience = normalizeExperience(dataRouterId)
  return EXPERIENCE_FORMAT.test(experience) ? experience : undefined
}
